import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  'Trade Fair Stall Building',
  'Expo Organization',
  'Live Events',
  'Brand Activation',
  'Corporate Events',
  'Other'
];

const budgetRanges = [
  'Under ₹5 Lakhs',
  '₹5 - 15 Lakhs',
  '₹15 - 50 Lakhs',
  '₹50 Lakhs - 1 Crore',
  'Above ₹1 Crore'
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    eventDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        eventDate: '',
        message: ''
      });
    } catch (err) {
      setError('Failed to submit. Please try again or contact us directly.');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="page-transition" data-testid="contact-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-navy overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Get In Touch
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Let's Create Something <span className="text-gold">Extraordinary</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Ready to discuss your next event? Get in touch with our team for a free consultation and project quote.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-1"
              >
                <h2 className="font-heading text-2xl text-navy mb-8">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Office Address</h3>
                      <p className="text-muted-foreground">
                        P.No-32, W.P. Shiva Puri Colony,<br />
                        Autonagar, Rangareddi,<br />
                        Telangana, India
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Phone</h3>
                      <a href="tel:+919876543210" className="text-muted-foreground hover:text-gold transition-colors">
                        +91 98765 43210
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Email</h3>
                      <a href="mailto:info@dfairs.com" className="text-muted-foreground hover:text-gold transition-colors">
                        info@dfairs.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Business Hours</h3>
                      <p className="text-muted-foreground">
                        Monday - Saturday<br />
                        9:00 AM - 6:00 PM IST
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <div className="bg-muted p-8 lg:p-12">
                  <h2 className="font-heading text-2xl text-navy mb-2">Request a Quote</h2>
                  <p className="text-muted-foreground mb-8">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>

                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                      data-testid="form-success"
                    >
                      <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
                      <h3 className="font-heading text-2xl text-navy mb-2">Thank You!</h3>
                      <p className="text-muted-foreground mb-6">
                        Your inquiry has been submitted successfully. We'll get back to you shortly.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="btn-secondary"
                      >
                        Submit Another Inquiry
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-navy font-medium mb-2">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="form-input w-full"
                            placeholder="John Doe"
                            data-testid="input-name"
                          />
                        </div>
                        <div>
                          <label className="block text-navy font-medium mb-2">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="form-input w-full"
                            placeholder="john@company.com"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-navy font-medium mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="form-input w-full"
                            placeholder="+91 98765 43210"
                            data-testid="input-phone"
                          />
                        </div>
                        <div>
                          <label className="block text-navy font-medium mb-2">Company Name</label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="form-input w-full"
                            placeholder="Your Company"
                            data-testid="input-company"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-navy font-medium mb-2">Service Required *</label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            required
                            className="form-input w-full"
                            data-testid="select-service"
                          >
                            <option value="">Select a service</option>
                            {services.map(service => (
                              <option key={service} value={service}>{service}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-navy font-medium mb-2">Budget Range</label>
                          <select
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            className="form-input w-full"
                            data-testid="select-budget"
                          >
                            <option value="">Select budget range</option>
                            {budgetRanges.map(range => (
                              <option key={range} value={range}>{range}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mb-6">
                        <label className="block text-navy font-medium mb-2">Expected Event Date</label>
                        <input
                          type="date"
                          name="eventDate"
                          value={formData.eventDate}
                          onChange={handleChange}
                          className="form-input w-full"
                          data-testid="input-date"
                        />
                      </div>

                      <div className="mb-8">
                        <label className="block text-navy font-medium mb-2">Project Details *</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="form-input w-full resize-none"
                          placeholder="Tell us about your project, goals, and any specific requirements..."
                          data-testid="input-message"
                        />
                      </div>

                      {error && (
                        <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm" data-testid="form-error">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                        data-testid="submit-button"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Inquiry
                            <Send className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="h-[400px] bg-muted relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-navy font-heading text-xl">DFAIRS Communications</p>
              <p className="text-muted-foreground">Hyderabad, Telangana, India</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
